import Dexie, { type Table } from "dexie"
import dexieCloud from "dexie-cloud-addon"
import { Capacitor } from "@capacitor/core"
import yDexie from "y-dexie"

// ---------------------------------------------------------------------------
// Enums & Interfaces — re-exported from @totodo/operations (single source of truth)
// ---------------------------------------------------------------------------

export {
  ObjectType,
  ObjectStatus,
  StartAtType,
  StartAtSubType,
  ChecklistSubType,
  ReminderKind,
  SourceType,
  TriggerType,
} from "@totodo/operations"

export type {
  DbObject,
  DbIndex,
  DbStartAtIndex,
  DbSetting,
  DbReminder,
  DbFile,
  DbSharedViewPref,
  DbRecurrence,
  DbRoutine,
} from "@totodo/operations"

import type { DbPlanEntry } from "@totodo/operations"
import type { DbChatConversation, DbChatMessage } from "@totodo/operations"

// Import types for local use in ToToDoDexie class definition
import type {
  DbObject,
  DbIndex,
  DbStartAtIndex,
  DbSetting,
  DbReminder,
  DbFile,
  DbSharedViewPref,
  DbRecurrence,
  DbRoutine,
} from "@totodo/operations"

export type DevicePermissionValue = "granted" | "denied" | "unknown"
export type DeviceStatus = "active" | "logged_out" | "force_logout" | "inactive"
export type ReminderStatusValue = "ready" | "limited" | "off"

export interface IpApiLocationData {
  city?: string
  region?: string
  country?: string
  country_name?: string
  timezone?: string
}

export interface DbDevice {
  id?: string
  platform: string
  createdAt: string
  updatedAt?: string
  isDeleted?: 0 | 1
  deletedAt?: string
  model?: string
  operatingSystem?: "ios" | "android" | "windows" | "mac" | "unknown"
  osVersion?: string
  iOSVersion?: number
  androidSDKVersion?: number
  manufacturer?: string
  isVirtual?: 0 | 1
  memUsed?: number
  webViewVersion?: string
  appVersion?: string
  appBuild?: string
  language?: string
  userAgent?: string
  timezone?: string
  lastSeenAt?: string
  nickname?: string
  status?: DeviceStatus
  statusAt?: string
  loginLocation?: string
  lastSeenLocation?: string
  lastSeenLocationAt?: string
  owner?: string
  realmId?: string
  pushPermission?: DevicePermissionValue
  alarmPermission?: DevicePermissionValue
  localNotificationPermission?: DevicePermissionValue
  capabilitiesUpdatedAt?: string
  notificationsEnabled?: 0 | 1
  androidCanScheduleExactAlarms?: 0 | 1
  androidIsBackgroundRestricted?: 0 | 1
  iosBackgroundRefreshStatus?: "available" | "denied" | "restricted" | "unknown"
  backgroundExecutionUpdatedAt?: string
}

export interface DbPushToken {
  id?: string
  deviceId: string
  platform: string
  token: string
  status: string
  provider: string
  createdAt: string
  isDeleted?: 0 | 1
  updatedAt?: string
}

export interface DbLog {
  id?: string
  description: string
  createdAt: string
}

export interface DbImportTmp {
  id?: string
  data: unknown
  createdAt: string
  createdBy: string
}

export interface DbDocCache {
  id?: string
  createdAt: string
}

export interface DbAiMemory {
  id?: string
  owner?: string
  realmId?: string
  doc?: unknown // Y.Doc — managed by y-dexie addon
}

// ---------------------------------------------------------------------------
// Dexie class — schema matches v1 version 80 exactly
// ---------------------------------------------------------------------------

export class ToToDoDexie extends Dexie {
  totodo!: Table<DbObject, string>
  privateIndex!: Table<DbIndex, string>
  startAtIndex!: Table<DbStartAtIndex, string>
  setting!: Table<DbSetting, string>
  setting_local!: Table<DbSetting, string>
  debug_log!: Table<DbLog, string>
  import_tmp!: Table<DbImportTmp, string>
  doc_cache!: Table<DbDocCache, string>
  device!: Table<DbDevice, string>
  push_token!: Table<DbPushToken, string>
  reminder!: Table<DbReminder, string>
  files!: Table<DbFile, string>
  plan!: Table<DbPlanEntry, string>
  chatConversation!: Table<DbChatConversation, string>
  chatMessage!: Table<DbChatMessage, string>
  sharedViewPref!: Table<DbSharedViewPref, string>
  recurrence!: Table<DbRecurrence, string>
  routine!: Table<DbRoutine, string>
  ai_memory!: Table<DbAiMemory, string>

  constructor() {
    super("ToToDoDb", { addons: [yDexie, dexieCloud], cache: "immutable" })

    // Must match v1 version 80 exactly for Dexie Cloud sync
    this.version(80).stores({
      totodo: `id, [id+isDeleted+status], [parentPath+type], [parentPath+isDeleted], [parentPath+type+isDeleted], [parentPath+index], [type+status+isDeleted], [type+isDeleted+status], [type+isDeleted+owner], [type+isDeleted], [parentPath+status+isDeleted], type, index, title, note, *tags, dueAt, createdAt, createdBy, updatedAt, updatedBy, isDeleted, deletedAt, deletedBy, status, statusAt, statusBy, userStatus, realmId, owner, *fullTextIndex, doc:Y`,
      privateIndex: "id, index",
      startAtIndex: "id, index, startAt, type, subType, reminder",
      setting: "id, value, device, createdAt, createdBy, updatedAt, updatedBy",
      setting_local:
        "id, value, device, createdAt, createdBy, updatedAt, updatedBy",
      debug_log: "id, description, createdAt",
      import_tmp: "id, data, createdAt, createdBy",
      doc_cache: "id, createdAt",
      device:
        "id, platform, createdAt, updatedAt, isDeleted, deletedAt, model, operatingSystem, osVersion, iOSVersion, androidSDKVersion, manufacturer, isVirtual, memUsed, webViewVersion, appVersion, appBuild, language, timezone, lastSeenAt, nickname, status, statusAt, userAgent, loginLocation, lastSeenLocation, lastSeenLocationAt",
      push_token:
        "id, [deviceId+platform+token+status], deviceId, platform, createdAt, isDeleted, deletedAt, updatedAt, token, provider, status, errorCode, errorAt, revokedAt, lastValidatedAt",
      reminder:
        "id, [taskId+isDeleted], taskId, title, body, link, owner, realmId, triggerType, fireAt, kind, sourceType, createdAt, createdBy, updatedAt, updatedBy, isDeleted, deletedAt",
    })

    // v81: Add files table for image/file uploads with Dexie Cloud blob offloading
    this.version(81).stores({
      files: "@id, name, mimeType, parentId, parentType, createdAt, realmId",
    })

    // v82: Smart Plans — add anchorTaskId index on totodo + new plan table
    this.version(82).stores({
      totodo: `id, [id+isDeleted+status], [parentPath+type], [parentPath+isDeleted], [parentPath+type+isDeleted], [parentPath+index], [type+status+isDeleted], [type+isDeleted+status], [type+isDeleted+owner], [type+isDeleted], [parentPath+status+isDeleted], type, index, title, note, *tags, dueAt, createdAt, createdBy, updatedAt, updatedBy, isDeleted, deletedAt, deletedBy, status, statusAt, statusBy, userStatus, realmId, owner, *fullTextIndex, doc:Y, anchorTaskId`,
      plan: "id, kind, category, anchorTaskId, status, isDeleted, isBuiltIn, isPublished, creatorId, owner, realmId",
    })

    // v83: Global AI Chat — conversations and messages
    this.version(83).stores({
      chatConversation: "id, updatedAt, owner, realmId",
      chatMessage: "id, conversationId, createdAt, owner, realmId",
    })

    // v84: Shared View Preferences — opt-in for shared realm tasks in date views
    this.version(84).stores({
      sharedViewPref: "id, scope, owner, realmId",
    })

    // v85: Recurring Tasks — recurrence config table + instance tracking indexes on totodo
    this.version(85).stores({
      recurrence: "id, mode, owner, realmId, isDeleted",
      totodo: `id, [id+isDeleted+status], [parentPath+type], [parentPath+isDeleted], [parentPath+type+isDeleted], [parentPath+index], [type+status+isDeleted], [type+isDeleted+status], [type+isDeleted+owner], [type+isDeleted], [parentPath+status+isDeleted], type, index, title, note, *tags, dueAt, createdAt, createdBy, updatedAt, updatedBy, isDeleted, deletedAt, deletedBy, status, statusAt, statusBy, userStatus, realmId, owner, *fullTextIndex, doc:Y, anchorTaskId, recurringTemplateId, [recurringTemplateId+occurrenceKey], isRecurringTemplate`,
    })

    // v86: Routine Chains — ordered sequences of recurring templates
    this.version(86).stores({
      routine: "id, owner, realmId, isDeleted",
    })

    // v87: AI Memory — one Y.Doc per user for conflict-free memory sync
    this.version(87).stores({
      ai_memory: "id, owner, realmId, doc:Y",
    })

    // v88: Chat History UX — pinned conversations index + category
    this.version(88).stores({
      chatConversation: "id, updatedAt, pinned, category, owner, realmId",
    })

    // Configure Dexie Cloud — must happen in constructor, before db.open()
    // Following dexie-cloud-starter pattern: config in constructor, open in UI wrapper
    //
    // requireAuth supports URL params for OAuth callback auto-login:
    // Server OAuth flow redirects to /?email=...&otpId=...&otp=...
    // Dexie Cloud picks up these params and auto-completes login.
    const urlParams =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : null

    // DIAGNOSTIC: Intercept fetch to log URLs and catch errors
    // Also: CORS preflight recovery — if fetch fails with "Failed to fetch" to dexie.cloud,
    // retry with a cache-busting query param to force a new CORS preflight.
    if (typeof window !== "undefined" && !(window as any).__fetchPatched) {
      const origFetch = window.fetch
      window.fetch = function(this: typeof globalThis, input: RequestInfo | URL, init?: RequestInit) {
        const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url
        console.log("[FETCH-DIAG] →", url.slice(0, 120))
        return origFetch.call(this, input, init).then(
          (r) => { console.log("[FETCH-DIAG] ✅", r.status, url.slice(0, 80)); return r },
          (e) => {
            console.error("[FETCH-DIAG] ❌", url.slice(0, 80), e?.name, e?.message, e?.constructor?.name)
            // CORS preflight recovery: if fetch to dexie.cloud fails with TypeError
            // (no CORS headers = "Failed to fetch"), retry with cache-busting param
            // to force Chrome to make a fresh OPTIONS request
            if (e instanceof TypeError && url.includes("dexie.cloud") && !url.includes("_cors_bust=")) {
              const bustUrl = url + (url.includes("?") ? "&" : "?") + "_cors_bust=" + Date.now()
              console.warn("[FETCH-DIAG] 🔄 CORS recovery retry:", bustUrl.slice(0, 120))
              return origFetch(bustUrl, init).then(
                (r) => { console.log("[FETCH-DIAG] ✅ (CORS recovery)", r.status, bustUrl.slice(0, 80)); return r },
                (e2) => { console.error("[FETCH-DIAG] ❌ (CORS recovery also failed)", bustUrl.slice(0, 80), e2?.message); throw e2 }
              )
            }
            throw e
          }
        )
      } as typeof fetch
      ;(window as any).__fetchPatched = true
    }

    // Native apps: a visible app counts as an active user (patched addon), so the WebSocket
    // stays open and a phone left open on a shared list gets the other person's changes live.
    // Upstream drops the socket after 3 minutes without input until the next touch. On a phone
    // "visible" means screen on and app in front; the web keeps upstream behaviour.
    if (typeof window !== "undefined" && Capacitor.isNativePlatform()) {
      ;(globalThis as { __DEXIE_CLOUD_ACTIVE_WHILE_VISIBLE?: boolean }).__DEXIE_CLOUD_ACTIVE_WHILE_VISIBLE = true
    }

    this.cloud.configure({
      databaseUrl:
        import.meta.env.VITE_DEXIE_CLOUD_URL || "https://zfbftppyv.dexie.cloud",

      tryUseServiceWorker: false,

      // Local-only tables (never synced)
      unsyncedTables: ["setting_local", "debug_log", "import_tmp", "doc_cache"],

      // Computed properties that are local-only
      unsyncedProperties: {
        totodo: ["docHtml", "fullTextIndex"] satisfies (keyof DbObject)[],
      } satisfies { [TableName in keyof ToToDoDexie]?: string[] },

      // Load blob data on-demand instead of eagerly during sync.
      // Compatible with useFileUrl which handles !file?.content (shows shimmer).
      blobMode: 'lazy',

      // Enable custom login GUI — we render our own SignIn component
      customLoginGui: true,

      // Allow up to 5 minutes for initial sync (large accounts: 40k+ objects).
      // Default was 20s which caused false timeouts for heavy accounts.
      // Matches InitialSyncLoader MAX_DISPLAY_MS (300s).
      // NOTE: This is a custom patch option (not in DexieCloudOptions type).
      ...({ initialSyncTimeout: 300_000 } as Record<string, unknown>),

      // Lock timeout — max time a sync job can hold the navigator.locks lock.
      // On iOS, WebKit IDB connections can die after backgrounding, causing
      // navigator.locks.request() to never call back. 30s is long enough for
      // a normal sync but short enough to detect deadlocks before the user
      // gives up. Initial sync uses initialSyncTimeout (300s) separately.
      ...({ lockTimeout: 30_000 } as Record<string, unknown>),

      // Fetch timeout in browser — abort fetch after 10s. This MUST be shorter
      // than lockTimeout (30s) so the fetch aborts cleanly before the lock
      // force-releases. Without this, a dead network causes fetch to hang for
      // 2-5 minutes (browser TCP timeout), and the abandoned sync function
      // never terminates. 10s is generous for normal syncs (typically <3s).
      // Dead network → fast AbortError → clean retry schedule.
      ...({ fetchTimeout: 10_000 } as Record<string, unknown>),

      // Disable sync wait timeout in browser (same reasoning).
      ...({ syncWaitTimeout: 0 } as Record<string, unknown>),

      // Require authentication before db.open() resolves.
      // When URL contains OAuth callback params, pass them through
      // so Dexie Cloud can auto-complete the OTP verification.
      // DEV_AUTO_LOGIN: Vite env vars for build-time OTP injection (simulator testing)
      requireAuth: import.meta.env.VITE_DEV_EMAIL
        ? {
            email: import.meta.env.VITE_DEV_EMAIL,
            otpId: import.meta.env.VITE_DEV_OTP_ID,
            otp: import.meta.env.VITE_DEV_OTP,
          }
        : urlParams?.get("email")
        ? {
            email: urlParams.get("email") ?? undefined,
            otpId: urlParams.get("otpId") ?? undefined,
            otp: urlParams.get("otp") ?? undefined,
          }
        : true,
    })

    // Y.Doc trigger is registered later in initDocProcessing() (after initial sync)
    // to avoid a storm of trigger fires during first-login download.
  }
}

// eslint-disable-next-line import/no-mutable-exports, prefer-const
export let db = new ToToDoDexie()

// DEV: expose db + operations on window for console scripts
if (import.meta.env.DEV && typeof window !== "undefined") {
  ;(window as any).__db = db
  import("@/db/operations").then((ops) => {
    ;(window as any).__ops = ops
  })
}

const _mainDb = db

/**
 * Swap the global `db` binding to a different Dexie instance.
 *
 * Used by GuestSharePage to redirect all queries to the guest Dexie Cloud
 * instance. Safe because `/share/:shareId` renders outside the main app
 * shell — no other components are mounted that could read stale data.
 *
 * ES module live bindings guarantee that every file importing `db` sees
 * the new value immediately (no re-import needed).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function swapDb(newDb: any): void {
  db = newDb
}

/** Restore the original main-app Dexie instance. */
export function restoreDb(): void {
  db = _mainDb
}

// ---------------------------------------------------------------------------
// S7: Module-level db.open() — starts auth/sync BEFORE React mounts.
// DatabaseProvider awaits this promise instead of calling open() itself.
// Saves 100-200ms by running in parallel with React's initial render.
// ---------------------------------------------------------------------------
import { initSqliteIfNeeded } from "@/db/sqliteInit"

export const dbReadyPromise: Promise<void> =
  typeof window !== "undefined"
    ? initSqliteIfNeeded(db).then(() => db.open()).then(() => {})
    : Promise.resolve()

// ---------------------------------------------------------------------------
// Operations context — wire shared package to use this db instance + undo
// ---------------------------------------------------------------------------
import { setOpsContext, setDocContext } from "@totodo/operations"
import type { TotodoDb, OpEvent } from "@totodo/operations"
import { registerUndo } from "@/shared/undo"

// S12: Lazy import — Firebase analytics + TipTap doc context are deferred
// to avoid pulling ~400 KB of JS into the critical path.

async function handleOpEvent(event: OpEvent): Promise<void> {
  const [{ logAnalyticsEvent }, { Capacitor }, { useViewContext }] =
    await Promise.all([
      import("@/shared/services/analytics"),
      import("@capacitor/core"),
      import("@/app/stores/viewContext"),
    ])
  void logAnalyticsEvent(event.name, {
    op_view: useViewContext.getState().viewId,
    op_platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web",
    ...event.data,
  })
}

setOpsContext({
  db: db as unknown as TotodoDb,
  registerUndo,
  onEvent: handleOpEvent,
})

// S12: Defer doc context — only needed when user edits notes (docOps.ts
// already lazy-imports getDocContext). Schedule after first render.
if (typeof window !== "undefined") {
  const scheduleIdle =
    typeof requestIdleCallback === "function"
      ? (fn: () => void) => requestIdleCallback(fn, { timeout: 3000 })
      : (fn: () => void) => setTimeout(fn, 50)
  scheduleIdle(async () => {
    const [{ generateJSON, generateHTML, getSchema }, { headlessExtensions }] =
      await Promise.all([
        import("@tiptap/core"),
        import("@/services/doc-processing/headlessExtensions"),
      ])
    setDocContext({
      extensions: headlessExtensions,
      schema: getSchema(headlessExtensions),
      generateJSON,
      generateHTML,
    })
  })
}
